import { useState } from "react";
import { Award } from "lucide-react";
import { CERTIFICATION_BADGES } from "../data/sustainabilityCertifications";

/** Renders a "pursuing certification" panel while CERTIFICATION_BADGES is empty — see that file. */
export default function CertificationBadgeGrid() {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Lapsed certifications are filtered out at render time rather than via a
  // standalone utility with no other caller — this list is the only surface
  // that needs the filter, so a separate function would just be unused
  // computation waiting for a caller (the ZP-20 lesson: don't build that).
  const now = new Date().toISOString().slice(0, 10);
  const active = CERTIFICATION_BADGES.filter((b) => b.validTo >= now);

  if (active.length === 0) {
    return (
      <div className="mx-auto max-w-md rounded-2xl bg-foam-100 p-8 text-center">
        <Award className="mx-auto h-5 w-5 text-ocean-800/30" strokeWidth={1.5} />
        <p className="mt-2 text-sm text-ocean-800/60">
          We're actively pursuing third-party sustainable-seafood certification — badges will appear here
          once one is awarded.
        </p>
      </div>
    );
  }

  return (
    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {active.map((badge) => {
        const expanded = expandedId === badge.id;
        return (
          <div key={badge.id} className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-ocean-900/5">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-teal-400/15 text-teal-600">
              <Award className="h-5 w-5" strokeWidth={1.5} />
            </div>
            <h3 className="mt-4 font-display font-semibold text-ocean-950">{badge.body}</h3>
            <p className="mt-1 text-xs text-ocean-800/60">
              Scope: {badge.scope} · Valid through {badge.validTo}
            </p>
            <button
              type="button"
              onClick={() => setExpandedId(expanded ? null : badge.id)}
              aria-expanded={expanded}
              className="mt-3 text-xs font-semibold text-teal-600 hover:text-teal-700"
            >
              {expanded ? "Hide details" : "What does this mean?"}
            </button>
            {expanded && (
              <p className="mt-2 text-xs leading-relaxed text-ocean-800/60">{badge.explanation}</p>
            )}
          </div>
        );
      })}
    </div>
  );
}
